import { MultiBackend, getBackendOptions } from "@minoru/react-dnd-treeview";
import classnames from "classnames";
import { createContext, ReactNode, useCallback, useContext } from "react";
import { ConnectDragSource, DndContext, DndProvider } from "react-dnd";

import styles from "./sortable-list.module.css";

export type SortableVariant = "default" | "dense";

interface SortableListProps {
  children: ReactNode;
  onReorder: (fromIndex: number, toIndex: number) => void;
  /** Called once when a drag interaction ends, so consumers can persist the final order. */
  onReorderEnd?: () => void;
  className?: string;
  /** Unique identifier to allow nested sortable lists without conflicts */
  groupId?: string;
}

export interface SortableContextValue {
  onReorder: (fromIndex: number, toIndex: number) => void;
  onReorderEnd?: () => void;
  groupId: string;
}

export interface SortableItemContextValue {
  dragRef: ConnectDragSource;
}

export const SortableContext = createContext<SortableContextValue | null>(null);
export const SortableItemContext = createContext<SortableItemContextValue | null>(null);

export const useSortableContext = () => useContext(SortableContext);
export const useSortableItemContext = () => useContext(SortableItemContext);

/** Mount a provider only when this tree cannot see a drag-drop manager. */
function EnsureDndProvider({ children }: { children: ReactNode }) {
  const { dragDropManager } = useContext(DndContext);
  if (dragDropManager) return children;
  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      {children}
    </DndProvider>
  );
}

const SortableList = ({
  children,
  onReorder,
  onReorderEnd,
  className,
  groupId = "default",
}: SortableListProps) => {
  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex !== toIndex) {
        onReorder(fromIndex, toIndex);
      }
    },
    [onReorder],
  );

  const listClassName = classnames(styles.list, className);

  return (
    <EnsureDndProvider>
      <SortableContext.Provider value={{ onReorder: handleReorder, onReorderEnd, groupId }}>
        <div className={listClassName}>{children}</div>
      </SortableContext.Provider>
    </EnsureDndProvider>
  );
};

export default SortableList;
